/**
 * AS TRÊS GAVETAS DO CARD, E OS FORMULÁRIOS DE ENTRADA.
 *
 * Uma gaveta por botão do cartão, e a divisão é a que a equipe comercial
 * pediu — porque as três perguntas são feitas em momentos diferentes:
 *
 *   💬 Conversa    "o que já foi dito?"     → e responder, sem sair da tela
 *   🕘 Histórico   "o que combinamos?"      → observações e linha do tempo
 *   ℹ️ Informações "quem é este cliente?"   → a ficha, editável
 *
 * O que NÃO está aqui, e é decisão: nenhuma das gavetas inventa dado. Quando
 * o servidor recusa o envio, a gaveta mostra a frase que ele devolveu — não
 * uma inventada aqui. Motivo em dois lugares vira motivo divergente.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { CardCrm, EtapaCrm, UsuarioResumo } from "@co/contracts";
import { api, type ConversaDoCard, type FichaCard } from "../api.js";
import {
  Aviso,
  Botao,
  Campo,
  Carregando,
  Painel,
  Selo,
  Vazio,
  classeEntrada,
  type Tom,
} from "../ui/base.js";
import { ErroAcao, useCarga } from "../ui/carga.js";
import { Definicoes } from "../ui/dados.js";
import { Gaveta, Modal } from "../ui/gaveta.js";
import { PALETA_DE_ETAPA, corDaEtapa } from "../ui/etapa.js";
import { dataHora, relativo } from "../ui/formato.js";

function Cabecalho({ card, sub }: { card: CardCrm; sub: string }): JSX.Element {
  return (
    <div className="min-w-0">
      <h2 className="text-[14px] font-semibold tracking-tight leading-tight truncate">
        {card.razaoSocial}
      </h2>
      <p className="text-[11.5px] text-tinta-3 truncate">{sub}</p>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   💬 Conversa
   ══════════════════════════════════════════════════════════════════════ */

export function GavetaConversa({
  card,
  aoFechar,
  aoMudar,
}: {
  card: CardCrm;
  aoFechar: () => void;
  aoMudar: () => void;
}): JSX.Element {
  const { carga, recarregar } = useCarga<ConversaDoCard>(
    () => api.crmConversa(card.id),
    card.id,
  );
  const [texto, setTexto] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const fim = useRef<HTMLDivElement>(null);

  // Rolar para a última mensagem: conversa que abre no começo obriga a
  // rolar para descobrir o que acabou de ser dito, que é o que importa.
  useEffect(() => {
    if (carga.fase === "pronto") fim.current?.scrollIntoView({ block: "end" });
  }, [carga.fase]);

  const enviar = useCallback(async () => {
    const corpo = texto.trim();
    if (corpo === "" || enviando) return;
    setEnviando(true);
    setErro(null);
    try {
      await api.crmResponder(card.id, corpo);
      setTexto("");
      recarregar();
      aoMudar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não consegui enviar.");
    } finally {
      setEnviando(false);
    }
  }, [texto, enviando, card.id, recarregar, aoMudar]);

  return (
    <Gaveta
      rotulo={`Conversa com ${card.razaoSocial}`}
      aoFechar={aoFechar}
      cabecalho={<Cabecalho card={card} sub={card.telefoneExibicao} />}
      rodape={
        carga.fase === "pronto" ? (
          <RodapeEnvio
            conversa={carga.dados}
            texto={texto}
            setTexto={setTexto}
            enviar={enviar}
            enviando={enviando}
            erro={erro}
          />
        ) : undefined
      }
    >
      {carga.fase === "carregando" && <Carregando />}
      {carga.fase === "erro" && <ErroAcao>{carga.mensagem}</ErroAcao>}
      {carga.fase === "pronto" && (
        <>
          {carga.dados.mensagens.length === 0 ? (
            <Vazio>
              Nenhuma mensagem trocada com este cliente ainda.
            </Vazio>
          ) : (
            <div className="flex flex-col gap-2.5">
              {carga.dados.mensagens.map((m) => {
                const minha = m.direcao === "enviada";
                return (
                  <div
                    key={m.id}
                    className={`max-w-[85%] rounded-[10px] px-3 py-2 ${
                      minha
                        ? "self-end bg-marca-suave text-marca-tinta"
                        : "self-start bg-superficie-3 text-tinta"
                    }`}
                  >
                    <p className="text-[12.5px] leading-relaxed whitespace-pre-wrap break-words m-0">
                      {m.texto ?? <em className="text-tinta-3">mensagem sem texto</em>}
                    </p>
                    <div className="mt-1 flex items-center gap-2 text-[10.5px] text-tinta-3">
                      <span>{dataHora(m.ocorridoEm)}</span>
                      {m.numeroExibicao && <span>· {m.numeroExibicao}</span>}
                      {/*
                        `origem` distingue humano de IA e de sistema. Está aqui
                        e não por completude: foi a ausência desse
                        discriminador que produziu o bug dos 74%, em que o
                        rastro do próprio sistema entrou no histórico e cegou
                        a trava que decidia enviar.
                      */}
                      {m.origem !== "humano" && <span>· {m.origem}</span>}
                      {m.campanhaNome && <span>· {m.campanhaNome}</span>}
                    </div>
                  </div>
                );
              })}
              <div ref={fim} />
            </div>
          )}
        </>
      )}
    </Gaveta>
  );
}

function RodapeEnvio({
  conversa,
  texto,
  setTexto,
  enviar,
  enviando,
  erro,
}: {
  conversa: ConversaDoCard;
  texto: string;
  setTexto: (v: string) => void;
  enviar: () => void;
  enviando: boolean;
  erro: string | null;
}): JSX.Element {
  const envio = conversa.envio;

  if (!envio.pode) {
    return (
      <div className="flex flex-col gap-2">
        <p className="text-[12px] text-tinta-2 leading-relaxed">{envio.explicacao}</p>
        {envio.linkWa !== "" && (
          <a
            href={envio.linkWa}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-superficie-3 text-tinta text-[13px] px-3.5 py-2 font-medium hover:bg-linha"
          >
            Abrir no meu WhatsApp
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <p className="text-[11px] text-tinta-3 leading-snug">{envio.explicacao}</p>
      {erro && <ErroAcao>{erro}</ErroAcao>}
      <div className="flex gap-2 items-end">
        <textarea
          aria-label="Mensagem"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => {
            // Enter envia, Shift+Enter quebra linha — é o que a equipe já faz
            // no WhatsApp, e contrariar o hábito custa mensagem mandada pela
            // metade.
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              enviar();
            }
          }}
          rows={2}
          placeholder={`Responder por ${envio.numeroExibicao ?? "WhatsApp"}…`}
          className={`${classeEntrada} resize-none`}
        />
        <Botao tipo="principal" onClick={enviar} desabilitado={enviando || texto.trim() === ""}>
          {enviando ? "Enviando…" : "Enviar"}
        </Botao>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   🕘 Histórico
   ══════════════════════════════════════════════════════════════════════ */

const TOM_EVENTO: Record<string, Tom> = {
  criado: "cool",
  etapa: "marca",
  observacao: "idle",
  responsavel: "cool",
  dado: "idle",
  mensagem: "ok",
  arquivado: "warn",
};

const ROTULO_EVENTO: Record<string, string> = {
  criado: "entrou",
  etapa: "etapa",
  observacao: "observação",
  responsavel: "responsável",
  dado: "ficha",
  mensagem: "WhatsApp",
  arquivado: "arquivado",
};

export function GavetaHistorico({
  card,
  aoFechar,
  aoMudar,
}: {
  card: CardCrm;
  aoFechar: () => void;
  aoMudar: () => void;
}): JSX.Element {
  const { carga, recarregar } = useCarga<FichaCard>(() => api.crmFicha(card.id), card.id);
  const [texto, setTexto] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const anotar = useCallback(async () => {
    const corpo = texto.trim();
    if (corpo.length < 2 || salvando) return;
    setSalvando(true);
    setErro(null);
    try {
      await api.crmObservar(card.id, corpo);
      setTexto("");
      recarregar();
      aoMudar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não consegui salvar.");
    } finally {
      setSalvando(false);
    }
  }, [texto, salvando, card.id, recarregar, aoMudar]);

  return (
    <Gaveta
      rotulo={`Histórico de ${card.razaoSocial}`}
      aoFechar={aoFechar}
      cabecalho={<Cabecalho card={card} sub="Observações e linha do tempo" />}
      rodape={
        <div className="flex flex-col gap-2">
          {erro && <ErroAcao>{erro}</ErroAcao>}
          <textarea
            aria-label="Nova observação"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            rows={2}
            placeholder="O que aconteceu? Ex.: falei com o financeiro, pediu retorno dia 12."
            className={`${classeEntrada} resize-none`}
          />
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-tinta-3 flex-1 leading-snug">
              A observação entra no histórico e não é apagada — corrija com uma nova.
            </span>
            <Botao
              tipo="principal"
              onClick={anotar}
              desabilitado={salvando || texto.trim().length < 2}
            >
              {salvando ? "Salvando…" : "Anotar"}
            </Botao>
          </div>
        </div>
      }
    >
      {carga.fase === "carregando" && <Carregando />}
      {carga.fase === "erro" && <ErroAcao>{carga.mensagem}</ErroAcao>}
      {carga.fase === "pronto" && (
        <>
          {carga.dados.linhaDoTempo.length === 0 ? (
            <Vazio>Nada registrado ainda.</Vazio>
          ) : (
            <ol className="list-none m-0 p-0 flex flex-col gap-3">
              {carga.dados.linhaDoTempo.map((e) => (
                <li key={e.id} className="flex gap-3">
                  <div className="pt-0.5 shrink-0">
                    <Selo tom={TOM_EVENTO[e.tipo] ?? "idle"}>
                      {ROTULO_EVENTO[e.tipo] ?? e.tipo}
                    </Selo>
                  </div>
                  <div className="min-w-0 flex-1">
                    {e.tipo === "etapa" && (
                      <p className="text-[12.5px] text-tinta m-0">
                        {e.deEtapa ?? "—"} → <strong>{e.paraEtapa ?? "—"}</strong>
                      </p>
                    )}
                    {e.texto && (
                      <p className="text-[12.5px] text-tinta leading-relaxed m-0 whitespace-pre-wrap break-words">
                        {e.texto}
                      </p>
                    )}
                    <p className="text-[10.5px] text-tinta-3 mt-0.5 m-0">
                      {dataHora(e.ocorridoEm)}
                      {e.autor ? ` · ${e.autor}` : " · sistema"}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </>
      )}
    </Gaveta>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   ℹ️ Informações
   ══════════════════════════════════════════════════════════════════════ */

export function GavetaFicha({
  card,
  etapas,
  usuarios,
  aoFechar,
  aoMudar,
}: {
  card: CardCrm;
  etapas: readonly EtapaCrm[];
  usuarios: readonly UsuarioResumo[];
  aoFechar: () => void;
  aoMudar: () => void;
}): JSX.Element {
  const { carga, recarregar } = useCarga<FichaCard>(() => api.crmFicha(card.id), card.id);
  const [editando, setEditando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const [form, setForm] = useState({
    razaoSocial: card.razaoSocial,
    nome: card.nome ?? "",
    email: card.email ?? "",
    operadora: card.operadora ?? "",
    cnpj: "",
    valorEstimado: card.valorEstimado === null ? "" : String(card.valorEstimado),
  });

  useEffect(() => {
    if (carga.fase === "pronto") {
      setForm((f) => ({ ...f, cnpj: carga.dados.cnpj ?? "" }));
    }
  }, [carga]);

  const salvar = useCallback(async () => {
    setSalvando(true);
    setErro(null);
    try {
      await api.crmEditarCliente(card.id, {
        razaoSocial: form.razaoSocial.trim(),
        nome: form.nome.trim() === "" ? null : form.nome.trim(),
        email: form.email.trim() === "" ? null : form.email.trim(),
        operadora: form.operadora.trim() === "" ? null : form.operadora.trim(),
        cnpj: form.cnpj.trim() === "" ? null : form.cnpj.trim(),
      });
      const valor = form.valorEstimado.trim();
      await api.crmMover(card.id, {
        valorEstimado: valor === "" ? null : Number(valor.replace(",", ".")),
      });
      setEditando(false);
      recarregar();
      aoMudar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não consegui salvar.");
    } finally {
      setSalvando(false);
    }
  }, [card.id, form, recarregar, aoMudar]);

  const trocarWhats = useCallback(
    async (autorizado: boolean) => {
      setErro(null);
      try {
        await api.crmEditarCliente(card.id, { whatsappAutorizado: autorizado });
        recarregar();
      } catch (e) {
        setErro(e instanceof Error ? e.message : "Não consegui alterar a autorização.");
      }
    },
    [card.id, recarregar],
  );

  return (
    <Gaveta
      rotulo={`Informações de ${card.razaoSocial}`}
      aoFechar={aoFechar}
      cabecalho={<Cabecalho card={card} sub="Ficha do cliente" />}
      rodape={
        editando ? (
          <div className="flex items-center gap-2 justify-end">
            <Botao onClick={() => setEditando(false)}>Cancelar</Botao>
            <Botao tipo="principal" onClick={salvar} desabilitado={salvando}>
              {salvando ? "Salvando…" : "Salvar"}
            </Botao>
          </div>
        ) : (
          <div className="flex items-center gap-2 justify-end">
            <Botao onClick={() => setEditando(true)}>Editar ficha</Botao>
          </div>
        )
      }
    >
      {erro && <ErroAcao>{erro}</ErroAcao>}
      {carga.fase === "carregando" && <Carregando />}
      {carga.fase === "erro" && <ErroAcao>{carga.mensagem}</ErroAcao>}
      {carga.fase === "pronto" && (
        <>
          {editando ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Campo rotulo="Razão social">
                  <input
                    className={classeEntrada}
                    value={form.razaoSocial}
                    onChange={(e) => setForm({ ...form, razaoSocial: e.target.value })}
                  />
                </Campo>
              </div>
              <Campo rotulo="Nome de quem atende">
                <input
                  className={classeEntrada}
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                />
              </Campo>
              <Campo rotulo="E-mail">
                <input
                  className={classeEntrada}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </Campo>
              <Campo rotulo="Operadora atual" dica="A do cliente — a de onde ele viria.">
                <input
                  className={classeEntrada}
                  value={form.operadora}
                  onChange={(e) => setForm({ ...form, operadora: e.target.value })}
                />
              </Campo>
              <Campo rotulo="CNPJ">
                <input
                  className={classeEntrada}
                  value={form.cnpj}
                  onChange={(e) => setForm({ ...form, cnpj: e.target.value })}
                />
              </Campo>
              <Campo rotulo="Valor estimado (R$)">
                <input
                  className={classeEntrada}
                  inputMode="decimal"
                  value={form.valorEstimado}
                  onChange={(e) => setForm({ ...form, valorEstimado: e.target.value })}
                />
              </Campo>
              <p className="sm:col-span-2 text-[11px] text-tinta-3 leading-relaxed">
                O telefone não é editável: ele é a identidade do cliente no sistema
                inteiro — é por ele que a conversa, a supressão e o histórico se
                ligam. Telefone errado se resolve cadastrando o certo.
              </p>
            </div>
          ) : (
            <Painel titulo="Quem é">
              <Definicoes
                itens={[
                  { k: "Razão social", v: card.razaoSocial },
                  { k: "Contato", v: card.nome ?? "—" },
                  { k: "Telefone", v: card.telefoneExibicao },
                  { k: "E-mail", v: card.email ?? "—" },
                  { k: "Operadora", v: card.operadora ?? "—" },
                  { k: "CNPJ", v: carga.dados.cnpj ?? "—" },
                  { k: "Etapa", v: carga.dados.etapaNome },
                  { k: "Responsável", v: card.responsavelNome },
                  {
                    k: "No funil desde",
                    v: `${dataHora(card.criadoEm)} (${relativo(card.criadoEm)})`,
                  },
                  {
                    k: "Último contato",
                    v: card.ultimoContatoEm ? relativo(card.ultimoContatoEm) : "nunca",
                  },
                ]}
              />
            </Painel>
          )}

          <Painel titulo="Quem atende">
            <select
              aria-label="Responsável"
              value={card.responsavelId}
              onChange={async (e) => {
                try {
                  await api.crmMover(card.id, { responsavelId: e.target.value });
                  aoMudar();
                  recarregar();
                } catch (err) {
                  setErro(err instanceof Error ? err.message : "Não consegui trocar.");
                }
              }}
              className={classeEntrada}
            >
              {usuarios.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.nome}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-tinta-3 mt-2 leading-relaxed">
              Trocar o responsável move o cliente para a carteira dessa pessoa — e é
              a carteira que decide quem enxerga o card.
            </p>
          </Painel>

          <Painel titulo="De onde veio">
            <p className="text-[12.5px] text-tinta-2 leading-relaxed">
              {carga.dados.procedencia.explicacao}
            </p>
            <div className="mt-3">
              <Definicoes
                itens={[
                  { k: "Lote", v: carga.dados.procedencia.loteRotulo ?? "—" },
                  { k: "Origem", v: carga.dados.procedencia.origem ?? "—" },
                  { k: "Titular", v: carga.dados.naturezaTitular },
                  { k: "Toques em 30 d", v: String(carga.dados.toques30d) },
                ]}
              />
            </div>
            <label className="flex items-start gap-2 mt-3 text-[12px] text-tinta-2">
              <input
                type="checkbox"
                checked={carga.dados.canaisAutorizados.includes("whatsapp")}
                onChange={(e) => void trocarWhats(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                Este cliente autorizou contato por WhatsApp.
                <span className="block text-tinta-3">
                  Marcar é ato registrado em auditoria. O canal novo nunca herda a
                  autorização do antigo — foi assim que uma base de telefone virou
                  base de WhatsApp sem ninguém decidir.
                </span>
              </span>
            </label>
          </Painel>

          {carga.dados.campanhas.length > 0 && (
            <Painel titulo="Campanhas que já tocaram este cliente">
              <ul className="list-none m-0 p-0 flex flex-col gap-1.5">
                {carga.dados.campanhas.map((c, i) => (
                  <li
                    key={`${c.nome}-${i}`}
                    className="flex items-center gap-2 text-[12.5px] text-tinta-2"
                  >
                    <span className="flex-1 min-w-0 truncate">{c.nome}</span>
                    <Selo tom="idle">{c.estado}</Selo>
                    <span className="text-[11px] text-tinta-3 tabular">
                      {c.ultimoToqueEm ? dataHora(c.ultimoToqueEm) : "—"}
                    </span>
                  </li>
                ))}
              </ul>
            </Painel>
          )}

          <Painel titulo="Tirar do funil">
            <p className="text-[12px] text-tinta-3 leading-relaxed mb-2">
              Arquivar não apaga nada: o histórico continua, e o cliente perdido de
              hoje é a base de reativação do ano que vem.
            </p>
            <Botao
              tipo="perigo"
              onClick={async () => {
                try {
                  await api.crmMover(card.id, { arquivar: true });
                  aoMudar();
                  aoFechar();
                } catch (e) {
                  setErro(e instanceof Error ? e.message : "Não consegui arquivar.");
                }
              }}
            >
              Arquivar cliente
            </Botao>
          </Painel>
        </>
      )}
    </Gaveta>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   Cadastro manual
   ══════════════════════════════════════════════════════════════════════ */

export function ModalNovoCliente({
  etapas,
  usuarios,
  aoFechar,
  aoCriar,
}: {
  etapas: readonly EtapaCrm[];
  usuarios: readonly UsuarioResumo[];
  aoFechar: () => void;
  aoCriar: () => void;
}): JSX.Element {
  const [form, setForm] = useState({
    razaoSocial: "",
    nome: "",
    telefone: "",
    email: "",
    operadora: "",
    cnpj: "",
    etapaId: etapas[0]?.id ?? "",
    responsavelId: "",
    valorEstimado: "",
    observacao: "",
  });
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const salvar = useCallback(async () => {
    setSalvando(true);
    setErro(null);
    try {
      const valor = form.valorEstimado.trim();
      await api.crmCriarCard({
        razaoSocial: form.razaoSocial.trim(),
        nome: form.nome.trim() || undefined,
        telefone: form.telefone.trim(),
        email: form.email.trim() || undefined,
        operadora: form.operadora.trim() || undefined,
        cnpj: form.cnpj.trim() || undefined,
        etapaId: form.etapaId || undefined,
        responsavelId: form.responsavelId || undefined,
        valorEstimado: valor === "" ? undefined : Number(valor.replace(",", ".")),
        observacao: form.observacao.trim() || undefined,
      });
      aoCriar();
      aoFechar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não consegui cadastrar.");
    } finally {
      setSalvando(false);
    }
  }, [form, aoCriar, aoFechar]);

  return (
    <Modal
      titulo="Novo cliente"
      dica="Razão social e telefone bastam. O resto pode entrar depois — dado inventado para conseguir salvar é pior que campo vazio."
      aoFechar={aoFechar}
      rodape={
        <>
          <Botao onClick={aoFechar}>Cancelar</Botao>
          <Botao
            tipo="principal"
            onClick={salvar}
            desabilitado={salvando || form.razaoSocial.trim() === "" || form.telefone.trim() === ""}
          >
            {salvando ? "Salvando…" : "Cadastrar"}
          </Botao>
        </>
      }
    >
      {erro && <ErroAcao>{erro}</ErroAcao>}
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Campo rotulo="Razão social *">
            <input
              autoFocus
              className={classeEntrada}
              placeholder="COMERCIAL SÃO JOÃO LTDA"
              value={form.razaoSocial}
              onChange={(e) => setForm({ ...form, razaoSocial: e.target.value })}
            />
          </Campo>
        </div>
        <Campo rotulo="Telefone *" dica="Com DDD. É a identidade do cliente aqui.">
          <input
            className={classeEntrada}
            placeholder="(67) 99216-8673"
            value={form.telefone}
            onChange={(e) => setForm({ ...form, telefone: e.target.value })}
          />
        </Campo>
        <Campo rotulo="Nome de quem atende">
          <input
            className={classeEntrada}
            placeholder="João Pedro"
            value={form.nome}
            onChange={(e) => setForm({ ...form, nome: e.target.value })}
          />
        </Campo>
        <Campo rotulo="E-mail">
          <input
            className={classeEntrada}
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </Campo>
        <Campo rotulo="Operadora atual">
          <input
            className={classeEntrada}
            placeholder="Claro, Tim, Vivo…"
            value={form.operadora}
            onChange={(e) => setForm({ ...form, operadora: e.target.value })}
          />
        </Campo>
        <Campo rotulo="CNPJ">
          <input
            className={classeEntrada}
            value={form.cnpj}
            onChange={(e) => setForm({ ...form, cnpj: e.target.value })}
          />
        </Campo>
        <Campo rotulo="Valor estimado (R$)">
          <input
            className={classeEntrada}
            inputMode="decimal"
            value={form.valorEstimado}
            onChange={(e) => setForm({ ...form, valorEstimado: e.target.value })}
          />
        </Campo>
        <Campo rotulo="Etapa">
          <select
            className={classeEntrada}
            value={form.etapaId}
            onChange={(e) => setForm({ ...form, etapaId: e.target.value })}
          >
            {etapas.map((e) => (
              <option key={e.id} value={e.id}>
                {e.nome}
              </option>
            ))}
          </select>
        </Campo>
        <Campo rotulo="Responsável" dica="Vazio = você.">
          <select
            className={classeEntrada}
            value={form.responsavelId}
            onChange={(e) => setForm({ ...form, responsavelId: e.target.value })}
          >
            <option value="">Eu</option>
            {usuarios.map((u) => (
              <option key={u.id} value={u.id}>
                {u.nome}
              </option>
            ))}
          </select>
        </Campo>
        <div className="sm:col-span-2">
          <Campo rotulo="Primeira observação">
            <textarea
              rows={2}
              className={`${classeEntrada} resize-none`}
              placeholder="Como chegou, o que ele precisa…"
              value={form.observacao}
              onChange={(e) => setForm({ ...form, observacao: e.target.value })}
            />
          </Campo>
        </div>
      </div>
      <Aviso tom="idle">
        Cliente cadastrado à mão entra no CRM e pode ser atendido, conversado e
        fechado — mas <strong>não entra em campanha de disparo</strong>. Para isso a
        base precisa vir com planilha e procedência declarada, que é o que prova de
        onde veio o telefone.
      </Aviso>
    </Modal>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   Upload em massa
   ══════════════════════════════════════════════════════════════════════ */

export function ModalImportar({
  etapas,
  usuarios,
  aoFechar,
  aoImportar,
}: {
  etapas: readonly EtapaCrm[];
  usuarios: readonly UsuarioResumo[];
  aoFechar: () => void;
  aoImportar: () => void;
}): JSX.Element {
  const { carga } = useCarga(() => api.lotes(), "lotes");
  const [loteId, setLoteId] = useState("");
  const [etapaId, setEtapaId] = useState(etapas[0]?.id ?? "");
  const [responsavelId, setResponsavelId] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [feito, setFeito] = useState<string | null>(null);
  const [rodando, setRodando] = useState(false);

  const executar = useCallback(
    async (tipo: "lote" | "leads") => {
      setRodando(true);
      setErro(null);
      setFeito(null);
      try {
        const comum = {
          etapaId: etapaId || undefined,
          responsavelId: responsavelId || undefined,
        };
        const r =
          tipo === "lote"
            ? await api.crmImportarLote({ ...comum, loteId })
            : await api.crmImportarLeads(comum);
        setFeito(r.motivo);
        aoImportar();
      } catch (e) {
        setErro(e instanceof Error ? e.message : "Não consegui importar.");
      } finally {
        setRodando(false);
      }
    },
    [etapaId, responsavelId, loteId, aoImportar],
  );

  return (
    <Modal
      titulo="Trazer clientes para o funil"
      dica="Dois caminhos, e os dois usam a base que já existe — nenhum cria uma segunda cópia do cliente."
      largura={720}
      aoFechar={aoFechar}
      rodape={<Botao onClick={aoFechar}>Fechar</Botao>}
    >
      {erro && <ErroAcao>{erro}</ErroAcao>}
      {feito && <Aviso tom="ok">{feito}</Aviso>}

      <div className="grid gap-3 sm:grid-cols-2">
        <Campo rotulo="Entram na etapa">
          <select
            className={classeEntrada}
            value={etapaId}
            onChange={(e) => setEtapaId(e.target.value)}
          >
            {etapas.map((e) => (
              <option key={e.id} value={e.id}>
                {e.nome}
              </option>
            ))}
          </select>
        </Campo>
        <Campo rotulo="Responsável" dica="Vazio = você.">
          <select
            className={classeEntrada}
            value={responsavelId}
            onChange={(e) => setResponsavelId(e.target.value)}
          >
            <option value="">Eu</option>
            {usuarios.map((u) => (
              <option key={u.id} value={u.id}>
                {u.nome}
              </option>
            ))}
          </select>
        </Campo>
      </div>

      <Painel titulo="1 · De uma planilha já importada">
        <p className="text-[12px] text-tinta-3 leading-relaxed mb-3">
          O upload de planilha continua sendo feito em <strong>Campanhas → Base</strong>,
          onde a procedência é declarada e o arquivo original fica retido com hash. É
          o que prova, depois, para quem cobrar, de onde veio cada telefone. Aqui você
          escolhe um lote já carregado e traz os contatos dele para o funil.
        </p>
        {carga.fase === "carregando" && <Carregando />}
        {carga.fase === "erro" && <ErroAcao>{carga.mensagem}</ErroAcao>}
        {carga.fase === "pronto" && (
          <div className="flex gap-2 items-end flex-wrap">
            <div className="flex-1 min-w-[240px]">
              <Campo rotulo="Lote">
                <select
                  className={classeEntrada}
                  value={loteId}
                  onChange={(e) => setLoteId(e.target.value)}
                >
                  <option value="">Escolha…</option>
                  {carga.dados.itens.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.rotulo} · {l.contatos} contatos · {l.estado}
                    </option>
                  ))}
                </select>
              </Campo>
            </div>
            <Botao
              tipo="principal"
              onClick={() => void executar("lote")}
              desabilitado={rodando || loteId === ""}
            >
              Trazer do lote
            </Botao>
          </div>
        )}
      </Painel>

      <Painel titulo="2 · Quem já respondeu">
        <p className="text-[12px] text-tinta-3 leading-relaxed mb-3">
          Traz para o funil todo contato que <strong>respondeu</strong> a uma campanha e
          ainda não tem card. Só quem respondeu: despejar a base inteira do disparo
          aqui entupiria o quadro com milhares de cartões que ninguém vai trabalhar.
        </p>
        <Botao tipo="principal" onClick={() => void executar("leads")} desabilitado={rodando}>
          Trazer quem respondeu
        </Botao>
      </Painel>
    </Modal>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   Configuração das etapas
   ══════════════════════════════════════════════════════════════════════ */

/**
 * Seletor de cor por AMOSTRA, não por nome.
 *
 * A versão anterior oferecia um `<select>` com "ember", "warn", "idle" — os
 * nomes internos dos tokens. Ninguém do comercial sabe o que é ember, e a
 * pessoa era obrigada a salvar e olhar o quadro para descobrir que cor tinha
 * escolhido. Amostra clicável responde a pergunta antes do clique.
 */
function EscolherCor({
  valor,
  aoEscolher,
}: {
  valor: string;
  aoEscolher: (tom: string) => void;
}): JSX.Element {
  return (
    <div className="flex gap-1.5 flex-wrap" role="group" aria-label="Cor da etapa">
      {PALETA_DE_ETAPA.map((tom) => {
        const cor = corDaEtapa(tom);
        const ativa = tom === valor;
        return (
          <button
            key={tom}
            type="button"
            onClick={() => aoEscolher(tom)}
            aria-pressed={ativa}
            title={cor.nome}
            aria-label={cor.nome}
            className={`size-6 rounded-md ${cor.faixa} transition-transform ${
              ativa ? "ring-2 ring-offset-2 ring-offset-superficie ring-tinta scale-110" : "opacity-70 hover:opacity-100"
            }`}
          />
        );
      })}
    </div>
  );
}

export function ModalEtapas({
  aoFechar,
  aoMudar,
}: {
  aoFechar: () => void;
  aoMudar: () => void;
}): JSX.Element {
  const { carga, recarregar } = useCarga(() => api.crmEtapas(), "etapas");
  const [erro, setErro] = useState<string | null>(null);
  const [novo, setNovo] = useState({ nome: "", tom: "azul", tipo: "aberta", instrucao: "" });

  const agir = useCallback(
    async (fn: () => Promise<unknown>) => {
      setErro(null);
      try {
        await fn();
        recarregar();
        aoMudar();
      } catch (e) {
        setErro(e instanceof Error ? e.message : "Não consegui salvar.");
      }
    },
    [recarregar, aoMudar],
  );

  return (
    <Modal
      titulo="Etapas do funil"
      dica="As etapas são suas. Renomeie, reordene e crie sem precisar de desenvolvedor — o que veio pronto é ponto de partida, não decisão."
      largura={760}
      aoFechar={aoFechar}
      rodape={<Botao onClick={aoFechar}>Fechar</Botao>}
    >
      {erro && <ErroAcao>{erro}</ErroAcao>}
      {carga.fase === "carregando" && <Carregando />}
      {carga.fase === "erro" && <ErroAcao>{carga.mensagem}</ErroAcao>}
      {carga.fase === "pronto" && (
        <>
          <ol className="list-none m-0 p-0 flex flex-col gap-2">
            {carga.dados.itens.map((e) => (
              <li
                key={e.id}
                className="border border-linha rounded-[10px] p-3 flex flex-col gap-2 bg-superficie-2"
              >
                <div className="flex gap-2 items-center flex-wrap">
                  <input
                    aria-label={`Nome da etapa ${e.nome}`}
                    defaultValue={e.nome}
                    onBlur={(ev) => {
                      const v = ev.target.value.trim();
                      if (v !== "" && v !== e.nome) void agir(() => api.crmEditarEtapa(e.id, { nome: v }));
                    }}
                    className={`${classeEntrada} max-w-[220px]`}
                  />
                  <EscolherCor
                    valor={e.tom}
                    aoEscolher={(tom) => void agir(() => api.crmEditarEtapa(e.id, { tom }))}
                  />
                  <select
                    aria-label={`Tipo da etapa ${e.nome}`}
                    value={e.tipo}
                    onChange={(ev) => void agir(() => api.crmEditarEtapa(e.id, { tipo: ev.target.value }))}
                    className={`${classeEntrada} max-w-[140px]`}
                  >
                    <option value="aberta">em andamento</option>
                    <option value="ganho">fecha ganho</option>
                    <option value="perdido">fecha perdido</option>
                  </select>
                  <input
                    aria-label={`Ordem da etapa ${e.nome}`}
                    type="number"
                    defaultValue={e.ordem}
                    onBlur={(ev) => {
                      const v = Number(ev.target.value);
                      if (Number.isFinite(v) && v !== e.ordem) {
                        void agir(() => api.crmEditarEtapa(e.id, { ordem: v }));
                      }
                    }}
                    className={`${classeEntrada} max-w-[80px]`}
                  />
                  <span className="ml-auto text-[11px] text-tinta-3 tabular">
                    {e.cards} no funil
                  </span>
                </div>
                <input
                  aria-label={`Instrução da etapa ${e.nome}`}
                  defaultValue={e.instrucao ?? ""}
                  placeholder="O que se faz nesta etapa? Aparece na visão Foco."
                  onBlur={(ev) => {
                    const v = ev.target.value.trim();
                    if (v !== (e.instrucao ?? "")) {
                      void agir(() => api.crmEditarEtapa(e.id, { instrucao: v }));
                    }
                  }}
                  className={classeEntrada}
                />
              </li>
            ))}
          </ol>

          <Painel titulo="Nova etapa">
            <div className="grid gap-3 sm:grid-cols-[1fr_auto_150px_auto] items-end">
              <Campo rotulo="Nome">
                <input
                  className={classeEntrada}
                  value={novo.nome}
                  onChange={(e) => setNovo({ ...novo, nome: e.target.value })}
                />
              </Campo>
              <Campo rotulo="Cor">
                <div className="pt-1.5">
                  <EscolherCor valor={novo.tom} aoEscolher={(tom) => setNovo({ ...novo, tom })} />
                </div>
              </Campo>
              <Campo rotulo="Tipo">
                <select
                  className={classeEntrada}
                  value={novo.tipo}
                  onChange={(e) => setNovo({ ...novo, tipo: e.target.value })}
                >
                  <option value="aberta">em andamento</option>
                  <option value="ganho">fecha ganho</option>
                  <option value="perdido">fecha perdido</option>
                </select>
              </Campo>
              <Botao
                tipo="principal"
                desabilitado={novo.nome.trim().length < 2}
                onClick={() =>
                  void agir(async () => {
                    await api.crmCriarEtapa({
                      nome: novo.nome.trim(),
                      tom: novo.tom,
                      tipo: novo.tipo,
                      instrucao: novo.instrucao.trim() || null,
                    });
                    setNovo({ nome: "", tom: "idle", tipo: "aberta", instrucao: "" });
                  })
                }
              >
                Criar
              </Botao>
            </div>
          </Painel>
        </>
      )}
    </Modal>
  );
}
